import { Role } from '@gm-commercial/profile-model';

import {
  type CapacityUnit,
  DiagnosticFlag,
  DiagnosticSeverity,
  type DistanceUnit,
  type EnergyEfficiency,
  type EnergyUnit,
  type FuelEconomyUnit,
  SpeedUnit,
  type TemperatureUnit,
  TirePressureUnit,
} from '../models/asset.model';
import { UserSupportedLocale } from '../models/common.model';

export const DEFAULT_SESSION_CONFIG_KEY = 'all';

export const DEFAULT_EMPTY_VALUE = '--';
export const SPEED_WARNING_LIMITS: {
  [k in SpeedUnit]?: number[];
} = {
  [SpeedUnit.KPH]: [80, 88, 96, 104, 112, 120, 128],
  [SpeedUnit.MPH]: [50, 55, 60, 65, 70, 75, 80],
};
export const SEVEN_DAYS_IN_MS = 7 * 24 * 60 * 60 * 1000;

// percentage of expected pressure
export const TIRES_THRESHOLD_LOW = 0.77;
export const TIRES_THRESHOLD_FLAT = 0.68;

export const DEFAULT_DATE_FORMAT = 'MM/dd/yyyy';
export const SHORT_ISO_DATE_FORMAT = 'yyyy-MM-dd';
export const MEXICO_DEFAULT_DATE_FORMAT = 'dd/MM/yyyy';
export const DEFAULT_DATE_TIME_FORMAT = 'MM/dd/yyyy hh:mm a';
export const PAYMENT_EXPIRATION_DATE_FORMAT = 'MM/yy';
export const SHORT_DATE_FORMAT = 'MM/dd/yy';

export const TIME_FORMAT_WITH_TIMEZONE_WITHOUT_LEADING_0 = 'h:mm a ZZZZ';
export const TIME_FORMAT_WITH_TIMEZONE = 'hh:mm a ZZZZ';
export const TIME_FORMAT_WITHOUT_TIMEZONE = 'h:mm a';
export const CANADA_TIME_FORMAT_WITH_TIMEZONE = 'HH:mm ZZZZ';
export const DATE_TIME_FORMAT_WITH_TIMEZONE = 'MM/dd/yyyy hh:mm a ZZZZ';
export const DATE_TIME_FORMAT_WITH_SEPARATED_TIMEZONE =
  "MM/dd/yyyy 'at' hh:mm a ZZZZ";
export const DATE_TIME_FORMAT_DASHES_WITH_TIMEZONE = 'MM-dd-yyyy hh:mm a ZZZZ';
export const MEXICO_DATE_TIME_FORMAT_DASHES_WITH_TIMEZONE =
  'dd-MM-yyyy hh:mm a ZZZZ';
export const DATE_TIME_API_FORMAT_COMPLETE = 'yyyy-MM-dd HH:mm:ss';
export const TIME_API_FORMAT = 'HH:mm:ss';
export const TIME_FORMAT = 'HH:mm';
export const TIME_COUNTDOWN_FORMAT = 'm:ss';
export const LUXON_WEEKDAY_SHORT_FORMAT = 'EEE';
export const LUXON_TIMEZONE_ABBREVIATION_FORMAT = 'ZZZZ';
export const MONTH_DAY_FORMAT = 'MMM dd';
export const MONTH_DAY_WEEK_FORMAT = 'EEE, MMM dd';
export const FULL_MONTH_DAY_YEAR_FORMAT = 'MMMM dd, yyyy';
export const MONTH_SHORT_FORMAT = 'MMM';
export const DAY_FORMAT = 'dd';
export const ISO_DATE_TIME_FORMAT_DASHES_WITH_TIMEZONE =
  'yyyy-MM-dd - HH:mm ZZZZ';
export const DATE_TIME_FORMAT_WITH_SECONDS_AND_TIMEZONE =
  'MM-dd-yyyy hh:mm ss a ZZZZ';

export const DEFAULT_MONTH_DAY_YEAR_DATE_FORMAT = 'MMM d, yyyy';
export const SINGLE_MONTH_DAY_YEAR_FORMAT = 'M/d/yyyy';
export const DEFAULT_MONTH_DAY_YEAR_DATE_TIME_FORMAT_WITHOUT_TIMEZONE =
  'MMM d, yyyy h:mm a';
export const DEFAULT_MONTH_DAY_YEAR_DATE_TIME_FORMAT_WITH_TIMEZONE =
  'MMM d, yyyy h:mm a ZZZZ';
export const DATE_TIME_FILENAME_FORMAT = 'yyyyMMdd_HHmmss_ZZZZ';

export const DEFAULT_PAGINATION_PAGE = 0;
export const DEFAULT_PAGINATION_ROW_PER_PAGE = 10;
/**
 * Default table page size when MARCH_VEHICLE_MANAGEMENT_FLAG is ON (used by useDefaultTablePageSize).
 * Default page size is also standardized across BPS tables.
 */
export const DEFAULT_TABLE_PAGE_SIZE = 50;
export const DEFAULT_PAGINATION_MIN_ROWS_PER_PAGE = 5;
export const DEFAULT_PAGINATION_MAX_ROWS_PER_PAGE = 100;
export const SLIDE_IN_OUT_TIMEOUT = 500;

export const DEFAULT_TIRE_PRESSURE_UNIT = TirePressureUnit.KPA;
export const ASSET_TIRE_PRESSURE_UNIT = TirePressureUnit.PSI;
export enum LanguageLocale {
  EN = 'en',
  EN_US = 'en-us',
}

// TODO: Remove in favor of localization-core `CURRENCY_CODES`.
export enum Currency {
  USD = 'USD',
}

export const TIMEZONE_NEW_YORK = 'America/New_York';

export const LOCALE_FORMAT_DATE = {
  en: DEFAULT_DATE_FORMAT,
  fr: SHORT_ISO_DATE_FORMAT,
  es: MEXICO_DEFAULT_DATE_FORMAT,
  [UserSupportedLocale.en_US]: DEFAULT_DATE_FORMAT,
  [UserSupportedLocale.en_CA]: SHORT_ISO_DATE_FORMAT,
  [UserSupportedLocale.fr_CA]: SHORT_ISO_DATE_FORMAT,
  'es-US': DEFAULT_DATE_FORMAT,
  [UserSupportedLocale.es_MX]: MEXICO_DEFAULT_DATE_FORMAT,
};

export const LOCALE_FORMAT_TIME = {
  en: TIME_FORMAT_WITH_TIMEZONE,
  fr: CANADA_TIME_FORMAT_WITH_TIMEZONE,
  es: TIME_FORMAT_WITH_TIMEZONE,
  [UserSupportedLocale.en_US]: TIME_FORMAT_WITH_TIMEZONE,
  [UserSupportedLocale.en_CA]: CANADA_TIME_FORMAT_WITH_TIMEZONE,
  [UserSupportedLocale.fr_CA]: CANADA_TIME_FORMAT_WITH_TIMEZONE,
  'es-US': TIME_FORMAT_WITH_TIMEZONE,
  [UserSupportedLocale.es_MX]: TIME_FORMAT_WITH_TIMEZONE,
};

export const LOCALE_TIME_FORMAT_WITHOUT_TIMEZONE = {
  en: TIME_FORMAT_WITHOUT_TIMEZONE,
  fr: TIME_FORMAT,
  es: TIME_FORMAT_WITHOUT_TIMEZONE,
  [UserSupportedLocale.en_US]: TIME_FORMAT_WITHOUT_TIMEZONE,
  [UserSupportedLocale.en_CA]: TIME_FORMAT,
  [UserSupportedLocale.fr_CA]: TIME_FORMAT,
  'es-US': TIME_FORMAT_WITHOUT_TIMEZONE,
  [UserSupportedLocale.es_MX]: TIME_FORMAT_WITHOUT_TIMEZONE,
};
export const LOCALE_DATE_TIME_FORMAT = {
  en: DATE_TIME_FORMAT_DASHES_WITH_TIMEZONE,
  fr: ISO_DATE_TIME_FORMAT_DASHES_WITH_TIMEZONE,
  es: MEXICO_DATE_TIME_FORMAT_DASHES_WITH_TIMEZONE,
  [UserSupportedLocale.en_US]: DATE_TIME_FORMAT_DASHES_WITH_TIMEZONE,
  [UserSupportedLocale.en_CA]: ISO_DATE_TIME_FORMAT_DASHES_WITH_TIMEZONE,
  [UserSupportedLocale.fr_CA]: ISO_DATE_TIME_FORMAT_DASHES_WITH_TIMEZONE,
  'es-US': DATE_TIME_FORMAT_DASHES_WITH_TIMEZONE,
  [UserSupportedLocale.es_MX]: MEXICO_DATE_TIME_FORMAT_DASHES_WITH_TIMEZONE,
};

export const LUXON_DAY = {
  MON: 0,
  TUE: 1,
  WED: 2,
  THU: 3,
  FRI: 4,
  SAT: 5,
  SUN: 6,
};
export type PhoneHours = {
  startDay: (typeof LUXON_DAY)[keyof typeof LUXON_DAY];
  startHour: number;
  startMinute?: number;
  endDay: (typeof LUXON_DAY)[keyof typeof LUXON_DAY];
  endHour: number;
  endMinute?: number;
  timezone: string;
};

// Phone hours of Monday 8 AM - Saturday 9 PM EST
export const ONSTAR_PHONE_HOURS: PhoneHours = {
  startDay: LUXON_DAY.MON,
  startHour: 8,
  endDay: LUXON_DAY.SAT,
  endHour: 21,
  timezone: TIMEZONE_NEW_YORK,
};

// Phone hours of Monday 8 AM - Friday 6 PM EST
export const SUPPORT_PHONE_HOURS: PhoneHours = {
  startDay: LUXON_DAY.MON,
  startHour: 8,
  endDay: LUXON_DAY.FRI,
  endHour: 18,
  timezone: TIMEZONE_NEW_YORK,
};

export type UnitsType = {
  distance: DistanceUnit;
  capacity: CapacityUnit;
  fuelEconomy: FuelEconomyUnit;
  tirePressure: TirePressureUnit;
  speed: SpeedUnit;
  temperature: TemperatureUnit;
  energy: EnergyUnit;
  energyEfficiency: EnergyEfficiency;
};

export const SLASH_WITH_SPACES = ' / ';
export const KNOWN_DIAGNOSTIC_FLAGS: DiagnosticFlag[] = [
  DiagnosticFlag.TIRE_LOW,
  DiagnosticFlag.TIRE_FLAT,
  DiagnosticFlag.BATTERY_YELLOW,
  DiagnosticFlag.BATTERY_RED,
  DiagnosticFlag.FLUID_LOW_BRAKE,
  DiagnosticFlag.FLUID_LOW_WASHER,
];

export const ROLE_LEVELS = Object.values(Role).reduce(
  (levels, roleValue, idx) => ({ ...levels, [roleValue]: idx }),
  {} as { [k in Role]: number }
);

export const ROLE_KEYS = Object.entries(Role).reduce(
  (accumulator, [key, value]) => {
    return { ...accumulator, [value as Role]: key };
  },
  {} as { [key in Role]: keyof Role }
);

export const DIAGNOSTIC_SEVERITY_LEVELS = Object.values(
  DiagnosticSeverity
).reduce(
  (levels, severity, idx) => ({ ...levels, [severity]: idx }),
  {} as { [k in DiagnosticSeverity]: number }
);

export enum SessionConfigType {
  DETAIL_VIEW = 'Detail View',
  LIST_VIEW = 'List View',
}

export const DEFAULT_PAGE = '0';

export enum SearchConfigType {
  SEARCH_VIEW = 'Search View',
}

export enum MessagingType {
  FLEET = 'fleet',
}

export enum QueryParam {
  FLEETS_ID = 'fleetsId',
  ASSETS_ID = 'assetsId',
  DRIVERS_ID = 'driversId',
  CHARGE_SESSION_ID = 'chargeSessionId',
}

export const ACCEPTED_EXPORT_FILE_TYPE = 'text/csv';

export const ADMIN_ROLES: Role[] = [
  Role.SYSTEM_MANAGER,
  Role.SYSTEM_READER,
  Role.TECHNICAL_MANAGER,
];

export const FLEET_EXPERIENCE_ROLES: Role[] = [
  Role.FLEET_MANAGER,
  Role.ORG_MANAGER,
];

export const CATALOG_AND_PLANS_ACCESS_ROLES: Role[] = [
  Role.SYSTEM_MANAGER,
  Role.ORG_MANAGER,
];

export enum USStateAbbreviations {
  ALABAMA = 'AL',
  ALASKA = 'AK',
  ARIZONA = 'AZ',
  ARKANSAS = 'AR',
  CALIFORNIA = 'CA',
  COLORADO = 'CO',
  CONNECTICUT = 'CT',
  DELAWARE = 'DE',
  FLORIDA = 'FL',
  GEORGIA = 'GA',
  HAWAII = 'HI',
  IDAHO = 'ID',
  ILLINOIS = 'IL',
  INDIANA = 'IN',
  IOWA = 'IA',
  KANSAS = 'KS',
  KENTUCKY = 'KY',
  LOUISIANA = 'LA',
  MAINE = 'ME',
  MARYLAND = 'MD',
  MASSACHUSETTS = 'MA',
  MICHIGAN = 'MI',
  MINNESOTA = 'MN',
  MISSISSIPPI = 'MS',
  MISSOURI = 'MO',
  MONTANA = 'MT',
  NEBRASKA = 'NE',
  NEVADA = 'NV',
  NEW_HAMPSHIRE = 'NH',
  NEW_JERSEY = 'NJ',
  NEW_MEXICO = 'NM',
  NEW_YORK = 'NY',
  NORTH_CAROLINA = 'NC',
  NORTH_DAKOTA = 'ND',
  OHIO = 'OH',
  OKLAHOMA = 'OK',
  OREGON = 'OR',
  PENNSYLVANIA = 'PA',
  RHODE_ISLAND = 'RI',
  SOUTH_CAROLINA = 'SC',
  SOUTH_DAKOTA = 'SD',
  TENNESSEE = 'TN',
  TEXAS = 'TX',
  UTAH = 'UT',
  VERMONT = 'VT',
  VIRGINIA = 'VA',
  WASHINGTON = 'WA',
  WEST_VIRGINIA = 'WV',
  WISCONSIN = 'WI',
  WYOMING = 'WY',
}

export const ONSTAR_PHONE_NUMBER_MASKED = '1-888-4ONSTAR';
export const ONSTAR_PHONE_NUMBER = '1-888-466-7827';
